import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { nextStartAfter, showsOnTicker } from "../../shared/overlay-scene";
import { DEFAULT_OVERLAY_SETTINGS, type OverlaySettings } from "../../shared/settings";
import type { Division, FieldId, GameState, Pool, ScheduledGame, Team } from "../../shared/types";
import { useOptionalTournament } from "./tournament";
import { useFieldName } from "./useFields";
import { useGameState, useNow } from "./useGameState";

/**
 * Where the overlay's pixels come from.
 *
 * The overlay used to reach for its own data: `OverlayLive` called
 * `useGameState`, five components called `useFieldName`, and the intro card and
 * the break scene each fetched the tournament separately from inside their own
 * render tree. That made every graphic untestable without a live field and a
 * live socket — you staged a fake game on a real pitch to look at the halftime
 * card.
 *
 * So the presentation reads a FEED and never a network. Two things supply one:
 *
 *   · `LiveFeed`    the WebSocket, exactly as before. What OBS runs.
 *   · `PreviewFeed` frames handed in over `postMessage` by whoever embedded
 *                   this page in an iframe (`/super?view=overlay`).
 *
 * The presentation cannot tell them apart, which is the point: the showcase
 * exercises the real components rather than a dev-only copy of them.
 */
export interface OverlayFeed {
  state: GameState | null;
  /** serverNow - clientNow; add to Date.now() for server time. */
  skew: number;
  /** Resolved display name for this field. */
  fieldName: string;
  /**
   * Tournament data for the intro card's record line and the break scene's
   * schedule.
   *
   * `null` means "live": the two components that need it fetch it themselves
   * on mount, which is what keeps a running game from polling four endpoints
   * it has nothing to do with. Non-null means a fixture supplied it and
   * NOTHING here touches the network — the showcase works with the wifi off.
   */
  tournament: TournamentData | null;
  /**
   * Live scores from the tournament's OTHER fields, for the break scene's
   * ticker. Same contract as `tournament`: `null` means live, so the one
   * component that wants them fetches them itself on mount.
   */
  others: TickerGame[] | null;
  /**
   * The auto scene's timers. Same contract as `tournament`: `null` means live
   * (the page fetches public settings itself, and only when the resolver
   * actually needs them); non-null means a fixture supplied them.
   */
  overlaySettings: OverlaySettings | null;
  /**
   * A fixture-pinned clock for the auto-scene resolver, epoch ms. `null`
   * means live (skew-corrected wall clock). Without this a fixture authored
   * as "10 seconds after final" would silently age into the schedule while
   * somebody was looking at it.
   */
  previewNow: number | null;
}

/** One other field's game, reduced to what a ticker row shows. */
export interface TickerGame {
  fieldId: FieldId;
  fieldName: string;
  a: { short: string; color: string; score: number };
  b: { short: string; color: string; score: number };
  /** Played out. The score is a result, not a running total. */
  final: boolean;
}

export interface TournamentData {
  teams: Team[];
  schedule: ScheduledGame[];
  divisions: Division[];
  pools: Pool[];
}

export const EMPTY_TOURNAMENT: TournamentData = {
  teams: [],
  schedule: [],
  divisions: [],
  pools: [],
};

const FeedContext = createContext<OverlayFeed | null>(null);

export function useOverlayFeed(): OverlayFeed {
  const feed = useContext(FeedContext);
  if (feed === null) throw new Error("overlay feed missing");
  return feed;
}

/**
 * Teams/schedule/divisions/pools for the two graphics that need them.
 *
 * Called from inside `IntroCard` and `BreakCard` rather than hoisted into the
 * provider on purpose: mounting is what starts the requests, so a live game
 * still makes none of them. `pollMs` reproduces the break scene's minute
 * refresh; the intro card passes nothing and fetches once.
 */
export function useTournamentData(pollMs?: number): TournamentData {
  const { tournament } = useOverlayFeed();
  const fixture = tournament !== null;
  // Absent only under the showcase, which feeds fixtures and never fetches.
  const api = useOptionalTournament()?.api;
  const [live, setLive] = useState<TournamentData>(EMPTY_TOURNAMENT);

  useEffect(() => {
    if (fixture || api === undefined) return;
    let alive = true;
    const load = (): void => {
      const set =
        <K extends keyof TournamentData>(key: K) =>
        (value: TournamentData[K]): void => {
          if (alive) setLive((prev) => ({ ...prev, [key]: value }));
        };
      void api
        .teams()
        .then(set("teams"))
        .catch(() => undefined);
      void api
        .schedule()
        .then(set("schedule"))
        .catch(() => undefined);
      void api
        .divisions()
        .then(set("divisions"))
        .catch(() => undefined);
      void api
        .pools()
        .then(set("pools"))
        .catch(() => undefined);
    };
    load();
    const t = pollMs === undefined ? undefined : setInterval(load, pollMs);
    return () => {
      alive = false;
      if (t !== undefined) clearInterval(t);
    };
  }, [fixture, pollMs, api]);

  return tournament ?? live;
}

/**
 * Everything the auto-scene resolver (`shared/overlay-scene.ts`) needs beyond
 * the game state itself: the two timers, the configured game's start, and a
 * clock.
 *
 * The fetches are as lazy as the resolver's own needs: nothing leaves this
 * hook during live play (h1/h2/half decide "live" from the state alone), and
 * only the two windows ask for the schedule at all - a pregame-configured
 * field for its own start, a fresh final for the NEXT game's, which is what
 * lets the final card give way when that game is due (`finalHoldMs`) - the
 * same "mounting is what starts the requests" posture `useTournamentData`
 * documents. Fixtures supply both inputs through the feed, so the showcase
 * still works with the wifi off; `previewNow` pins the clock so a fixture
 * authored as "ten seconds after final" does not age into the schedule while
 * somebody is looking at it.
 */
export function useAutoSceneInputs(
  state: GameState | null,
  skew: number,
): { overlay: OverlaySettings; nextStartAtMs: number | null; now: number } {
  const { tournament, overlaySettings, previewNow } = useOverlayFeed();
  const fixture = tournament !== null;
  const api = useOptionalTournament()?.api;

  const pregameConfigured =
    state !== null &&
    state.scene === "auto" &&
    state.period === "pregame" &&
    state.configured === true;
  const autoFinal = state !== null && state.scene === "auto" && state.period === "final";
  const wantsSettings = autoFinal || pregameConfigured;
  const gameId = pregameConfigured ? state.scheduleGameId : null;

  const [fetchedOverlay, setFetchedOverlay] = useState<OverlaySettings | null>(null);
  const [fetchedSchedule, setFetchedSchedule] = useState<ScheduledGame[]>([]);

  useEffect(() => {
    if (!wantsSettings || overlaySettings !== null || api === undefined) return;
    let alive = true;
    const load = (): void => {
      void api
        .settings()
        .then((s) => {
          if (alive) setFetchedOverlay(s.overlay);
        })
        .catch(() => undefined);
    };
    load();
    const t = setInterval(load, 60_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [wantsSettings, overlaySettings, api]);

  useEffect(() => {
    if (!wantsSettings || fixture || api === undefined) return;
    let alive = true;
    const load = (): void => {
      void api
        .schedule()
        .then((rows) => {
          if (alive) setFetchedSchedule(rows);
        })
        .catch(() => undefined);
    };
    load();
    const t = setInterval(load, 60_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [wantsSettings, fixture, api]);

  // A second tick only while a window boundary can actually pass: the final
  // card expiring, the intro coming due, or the PREVIOUS game's final card
  // being held over this one's pregame (`holdsFinal`, which the overlay asks
  // whatever `configured` says). Anywhere else the resolver's answer cannot
  // change with time, so the idle cadence is token.
  const ticking =
    wantsSettings || (state !== null && state.scene === "auto" && state.period === "pregame");
  const wallNow = useNow(skew, ticking ? 1000 : 3_600_000);
  const now = previewNow ?? wallNow;

  const schedule = fixture ? tournament.schedule : fetchedSchedule;
  // The start the resolver cares about in THIS period. Pregame: the configured
  // game's own row. Final: the next unplayed game on this field, which is what
  // the final card's hold gives way to. Zone-less local strings, read in the
  // machine's own zone - correct because the OBS machine stands at the venue
  // (the break card leans on the same).
  let nextStartAtMs: number | null = null;
  if (gameId !== null) {
    const row = schedule.find((g) => g.id === gameId);
    nextStartAtMs = row === undefined ? null : new Date(row.startAt).getTime();
  } else if (state !== null && autoFinal && state.finalAt !== undefined) {
    nextStartAtMs = nextStartAfter(schedule, state.fieldId, state.scheduleGameId, state.finalAt);
  }
  return {
    overlay: overlaySettings ?? fetchedOverlay ?? DEFAULT_OVERLAY_SETTINGS,
    nextStartAtMs,
    now,
  };
}

/**
 * What every other field is doing right now.
 *
 * POLLED, not socketed. The board opens a WebSocket per field, which is right
 * for one page on one laptop and wrong here: this runs inside a Browser Source,
 * and a tournament with four fields and an OBS machine per field would hold a
 * socket for every pairing of the two. Scores change every couple of minutes,
 * so a poll is both cheaper and enough.
 *
 * Mounted by the break scene only, so a running game makes none of these calls
 * — the same reason `useTournamentData` is called from inside the two cards
 * that need it rather than hoisted into the provider.
 *
 * Which fields make a row is `showsOnTicker` in shared/overlay-scene.ts: the
 * derived facts, not the stored pin (the pin check emptied the strip the day
 * auto became the default). It needs the final-card window and a server-time
 * clock, so the caller hands in the resolver's `overlay` settings and the
 * socket's skew - the break card already has both.
 */
export function useOtherFields(
  current: FieldId,
  overlay: OverlaySettings,
  skew: number,
  pollMs = 20_000,
): TickerGame[] {
  const { others } = useOverlayFeed();
  const fixture = others !== null;
  const api = useOptionalTournament()?.api;
  const [live, setLive] = useState<TickerGame[]>([]);
  // Read at poll time through refs, deliberately NOT as effect dependencies:
  // `skew` is re-estimated on every socket message and `overlay` is a fresh
  // object on every settings poll, and either in the dependency list would
  // rebuild this effect - and fire its poll - for a reason that is not a
  // change. The window and the clock only need to be current when a poll
  // actually runs.
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;
  const skewRef = useRef(skew);
  skewRef.current = skew;

  useEffect(() => {
    if (fixture || api === undefined) return;
    let alive = true;
    // The field LIST is fetched in here too, rather than read from
    // `useFields`, so every dependency of this effect is a primitive. That
    // hook hands back a fresh array on each of its own polls, and depending on
    // its identity would tear this one down and rebuild it every 60s — an
    // effect restarting for a reason that is not a change is how a poll turns
    // into a burst.
    const load = (): void => {
      void api
        .fields()
        .then((all) =>
          Promise.all(
            all
              .filter((f) => f.id !== current)
              .map((f) =>
                api
                  .fieldState(f.id)
                  .then((m) => ({ field: f, state: m.state }))
                  // A field nobody has opened yet 404s, and one Worker hiccup
                  // must not blank the whole strip. Drop the row, keep the rest.
                  .catch(() => null),
              ),
          ),
        )
        .then((rows) => {
          if (!alive) return;
          const now = Date.now() + skewRef.current;
          setLive(
            rows.flatMap((row) => {
              if (row === null) return [];
              const { field, state } = row;
              // Play, the break, or a result still on that field's own final
              // card. Not the stored pin: see `showsOnTicker`.
              if (!showsOnTicker(state, now, overlayRef.current)) return [];
              return [
                {
                  fieldId: field.id,
                  fieldName: field.name,
                  a: {
                    short: state.teamA.short,
                    color: state.teamA.color,
                    score: state.teamA.score,
                  },
                  b: {
                    short: state.teamB.short,
                    color: state.teamB.color,
                    score: state.teamB.score,
                  },
                  final: state.period === "final",
                },
              ];
            }),
          );
        })
        .catch(() => undefined);
    };
    load();
    const t = setInterval(load, pollMs);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [fixture, current, pollMs, api]);

  return others ?? live;
}

/** The live socket. This is the one OBS runs. */
export function LiveFeed({ field, children }: { field: FieldId; children: ReactNode }) {
  const { state, skew } = useGameState(field, { src: "overlay" });
  const fieldName = useFieldName(field);
  return (
    <FeedContext.Provider
      value={{
        state,
        skew,
        fieldName,
        tournament: null,
        others: null,
        overlaySettings: null,
        previewNow: null,
      }}
    >
      {children}
    </FeedContext.Provider>
  );
}

/** One frame handed to a preview: everything the overlay renders from. */
export interface PreviewFrame {
  state: GameState;
  fieldName: string;
  tournament?: Partial<TournamentData>;
  /** Other fields for the break ticker. Absent means none are playing. */
  others?: TickerGame[];
  /** Auto-scene timers; absent means the defaults. */
  overlaySettings?: OverlaySettings;
  /** Pin the resolver's clock to this moment; absent means wall clock. */
  nowMs?: number;
}

/** What the embedder posts. Versioned so a stale showcase cannot half-drive a
 *  newer overlay and leave somebody debugging a graphic that is fine. */
export interface PreviewMessage {
  kind: "score-overlay-preview";
  v: 1;
  frame: PreviewFrame | null;
}

function isPreviewMessage(data: unknown): data is PreviewMessage {
  const m = data as PreviewMessage | null;
  return m !== null && typeof m === "object" && m.kind === "score-overlay-preview" && m.v === 1;
}

/** `?preview=1` is on the URL. Read once: OBS never navigates this page. */
export function isPreviewMode(): boolean {
  return new URLSearchParams(window.location.search).get("preview") === "1";
}

/**
 * `?hold=1`, and only alongside `?preview=1`.
 *
 * The timeout toast, the BREAK badge and the scorer card all clear themselves
 * after a few seconds, so checking how they STACK means racing a timer — and
 * the arrangement is exactly the thing that goes wrong, because each of them
 * was positioned against a different assumption about the others. Hold pins
 * them on screen instead.
 *
 * Gated on preview mode as WELL as the flag: this must never be reachable on a
 * Browser Source, where a graphic that does not clear itself is the worst
 * failure this overlay has.
 */
export function isHoldMode(): boolean {
  return isPreviewMode() && new URLSearchParams(window.location.search).get("hold") === "1";
}

/**
 * Frames from `window.parent`. No socket, no fetch, no timers of its own.
 *
 * Same-origin and same-source are both checked: this page is publicly
 * reachable, and a frame from anywhere else would be a stranger drawing on a
 * broadcast graphic. An unembedded `?preview=1` simply renders nothing, which
 * is the correct answer for a Browser Source that got the wrong URL.
 */
export function PreviewFeed({ children }: { children: ReactNode }) {
  const [frame, setFrame] = useState<PreviewFrame | null>(null);

  useEffect(() => {
    const onMessage = (ev: MessageEvent): void => {
      if (ev.origin !== window.location.origin) return;
      if (ev.source !== window.parent) return;
      if (!isPreviewMessage(ev.data)) return;
      setFrame(ev.data.frame);
    };
    window.addEventListener("message", onMessage);
    // The embedder may have mounted before this listener existed.
    window.parent.postMessage(
      { kind: "score-overlay-preview-ready", v: 1 },
      window.location.origin,
    );
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <FeedContext.Provider
      value={{
        state: frame?.state ?? null,
        // A fixture is authored against wall-clock time, so the preview's
        // server time IS its client time. Never a stale skew.
        skew: 0,
        fieldName: frame?.fieldName ?? "",
        tournament: { ...EMPTY_TOURNAMENT, ...(frame?.tournament ?? {}) },
        others: frame?.others ?? [],
        overlaySettings: frame?.overlaySettings ?? DEFAULT_OVERLAY_SETTINGS,
        previewNow: frame?.nowMs ?? null,
      }}
    >
      {children}
    </FeedContext.Provider>
  );
}
